import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { NotificationDto, NotificationListRequest } from 'generated/openApi';
import ApiService from 'services/apiService';

interface NotificationDataContextValue {
  notifications: NotificationDto[];
  loading: boolean;
  error: string | null;
  markAsRead: (notificationId: number) => Promise<void>;
  fetchNotifications: (page?: number, pageSize?: number) => Promise<void>;
}

const NotificationDataContext = createContext<NotificationDataContextValue | undefined>(undefined);

const DEFAULT_PAGE_SIZE = 5;

export const NotificationDataProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = async (page: number = 1, pageSize: number = DEFAULT_PAGE_SIZE) => {
    try {
      setLoading(true);
      setError(null);
      const request: NotificationListRequest = { page, pageSize };
      const resp = await ApiService.notifications.getList(request);
      setNotifications(resp.notifications ?? []);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: number) => {
    try {
      await ApiService.notifications.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError('Failed to mark notification as read');
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  return (
    <NotificationDataContext.Provider
      value={{ notifications, loading, error, markAsRead, fetchNotifications }}
    >
      {children}
    </NotificationDataContext.Provider>
  );
};

export const useNotificationData = () => {
  const context = useContext(NotificationDataContext);
  if (!context) {
    throw new Error('useNotificationData must be used within NotificationDataProvider');
  }
  return context;
};